/**
 * Rate cards on the phone (F4.5): only a PUBLISHED card for the crop and stream, in force on the
 * collection date (IST), with EMPTY deduction rules is usable. The phone never types a price.
 */
export interface CardLike {
  id: string
  crop_id: string
  stream: string
  status: string
  effective_from?: string | null
  effective_to?: string | null
  price_paise_per_kg: number | null
  deduction_rules?: unknown
  version?: string | number | null
}

export function hasNoDeductions(rules: unknown): boolean {
  if (rules === null || rules === undefined) return true
  if (Array.isArray(rules)) return rules.length === 0
  if (typeof rules === 'object') return Object.keys(rules as object).length === 0
  return false
}

/** `dateKey` is the IST business date "YYYY-MM-DD". Card dates compare as date strings. */
export function inForce(card: CardLike, dateKey: string): boolean {
  if (card.status !== 'PUBLISHED') return false
  const from = card.effective_from?.slice(0, 10)
  const to = card.effective_to?.slice(0, 10)
  if (from && from > dateKey) return false
  if (to && to < dateKey) return false
  return true
}

export type CardPick<C extends CardLike> =
  | { ok: true; card: C & { price_paise_per_kg: number } }
  | { ok: false; problem: 'NO_CARD' | 'HAS_DEDUCTIONS' }

export function pickCard<C extends CardLike>(
  cards: readonly C[],
  cropId: string,
  stream: 'MAIN' | 'CONV',
  dateKey: string,
): CardPick<C> {
  const live = cards.filter(
    (c) => c.crop_id === cropId && c.stream === stream && inForce(c, dateKey),
  )
  const clean = live.find(
    (c) => hasNoDeductions(c.deduction_rules) && c.price_paise_per_kg !== null,
  )
  if (clean) return { ok: true, card: clean as C & { price_paise_per_kg: number } }
  return { ok: false, problem: live.length > 0 ? 'HAS_DEDUCTIONS' : 'NO_CARD' }
}

/** F4.1: at least one PUBLISHED MAIN card in force today with empty deduction rules. */
export function anyUsableMainCard(cards: readonly CardLike[], dateKey: string): boolean {
  return cards.some(
    (c) =>
      c.stream === 'MAIN' &&
      inForce(c, dateKey) &&
      hasNoDeductions(c.deduction_rules) &&
      c.price_paise_per_kg !== null,
  )
}
